import type {
  OpenPositionAggregateSearchBody,
  OpenPositionAggregateSearchParameters,
} from "@/common/api/open-position/use-open-position-customer-aggregate-search.query";
import type {
  BaseApiResponse,
  DataFetchHooks,
} from "@/components/data-table/predefined/_components/base-predefined-table.types";
import type { UseQueryResult } from "@tanstack/react-query";
import { useMemo } from "react";

export type AggregateSearchHookArgs = Parameters<
  DataFetchHooks<unknown, OpenPositionAggregateSearchParameters, OpenPositionAggregateSearchBody>["useSearchQuery"]
>[0];

/**
 * Adapts an aggregate search query for the predefined table: aggregate rows carry no uuid of their own,
 * so `identify` derives the row identity from the grouped entity.
 */
export function useAggregateTableQuery<TAggregate, TRow extends TAggregate & { uuid: string }>(
  useSearchQuery: (args: AggregateSearchHookArgs) => UseQueryResult<BaseApiResponse<TAggregate>, Error>,
  identify: (aggregate: TAggregate) => TRow,
  args?: AggregateSearchHookArgs,
) {
  const query = useSearchQuery(args ?? {});
  const data = useMemo(
    () => (query.data ? { ...query.data, data: query.data.data.map(identify) } : undefined),
    [query.data, identify],
  );

  return { data, isPending: query.isPending, error: query.error };
}
